import z from "zod";
import { options } from "../constants.schema";
import { OPEN_EMAIL_DOMAINS } from "@/utils/emailDomain";

// Custom validation function for business/company email domains
const validateBusinessEmail = (email: string) => {
  const domain = email.toLowerCase().split('@')[1];
  if (!domain) return false;
  
  // Check if the domain is in the list of open email domains
  return !OPEN_EMAIL_DOMAINS.includes(domain);
};

// Adapted to allow phone_country coming back as null from persisted references while still validating create edits.
const referenceSchema = z.object({
  first_name: z.string().min(1, "First name is required").max(40, "First name must be 40 characters or less"),
  last_name: z.string().min(1, "Last name is required").max(40, "Last name must be 40 characters or less"),
  relationship: z.string().min(1, "Relationship is required").max(40, "Relationship must be 40 characters or less"),
  email: z.string()
    .email("Invalid email format")
    .refine(validateBusinessEmail, {
      message: "Please use a company/business email address. Personal email domains (like gmail.com, yahoo.com, etc.) are not allowed for references."
    }),
  // Accept null (DB may return null) or a properly shaped object; normalize later before submit.
  phone_country: z
    .union([z.object(options), z.null()])
    .optional()
    .transform((val) => (val === null ? undefined : val)),
  phone_number: z.string().max(20, "Phone number must be 20 characters or less").optional(),
});

const experienceSchema = z.object({
  job_title: z.string().optional(),
  company_name: z.string().optional(),
  description: z.string().max(1000, "Description must be 1000 characters or less").optional(),
  category: z.object(options).optional(),
  specialization: z.object(options).optional(),
  skills_experiences: z.array(z.object(options)).optional(),
  start_date: z.string().optional() as z.ZodType<string | null>,
  end_date: z.string().optional() as z.ZodType<string | null>,
  work_mode: z.object(options).optional(),
  company_industry: z.object(options).optional(),
  reference: referenceSchema.optional(),
});

const educationSchema = z.object({
  certification_title: z.string().optional(),
  certification_type: z.object(options).optional(),
  institution_name: z.string().optional(),
  category: z.object(options).optional(),
  start_date: z.string().optional() as z.ZodType<string | null>,
  end_date: z.string().optional() as z.ZodType<string | null>,
  skills_education: z.array(z.object(options)).optional(),
  reference: referenceSchema.optional(),
});

export const experienceAndEducationSchema = z.object({
  experiences: z.array(experienceSchema).optional(),
  educations: z.array(educationSchema).optional(),
});

export type ReferenceSchema = z.infer<typeof referenceSchema>;
export type ExperienceSchema = z.infer<typeof experienceSchema>;
export type EducationSchema = z.infer<typeof educationSchema>;

export type ExperienceEducationType = z.infer<
  typeof experienceAndEducationSchema
>;
